/**
 * User Settings Modal Component
 *
 * Allows users to update their settings and preferences
 */
import React from 'react';
import { Modal, Form, Input, Button, Space, Divider } from 'antd';
import { LockOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { useUserProfile } from '../hooks/useUserProfile';
import { useNotification } from '@/shared/utils/notification';
import type { UserSettingsModalProps } from '../types';
import { logger } from '@/shared/utils';

interface UserSettingsFormValues {
  email?: string;
  phone?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

export const UserSettingsModal: React.FC<UserSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const { user } = useUserProfile();
  const [form] = Form.useForm();
  const { showToast } = useNotification();

  const handleSubmit = async (values: UserSettingsFormValues) => {
    try {
      // TODO: Implement update user settings API call
      logger.info('Update settings:', values);

      showToast({
        type: 'success',
        title: 'Settings Updated',
        message: 'Your settings have been updated successfully',
      });

      onClose();
    } catch {
      showToast({
        type: 'error',
        title: 'Update Failed',
        message: 'Failed to update settings. Please try again.',
      });
    }
  };

  if (!user) {
    return null;
  }

  return (
    <Modal
      title="Settings"
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          email: user.email,
          phone: user.phone,
        }}
        onFinish={handleSubmit}
      >
        {/* Email */}
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter your email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="Enter email"
            size="large"
          />
        </Form.Item>

        {/* Phone */}
        <Form.Item name="phone" label="Phone">
          <Input
            prefix={<PhoneOutlined />}
            placeholder="Enter phone number"
            size="large"
          />
        </Form.Item>

        <Divider />

        {/* Change Password Section */}
        <h4>Change Password</h4>

        <Form.Item name="currentPassword" label="Current Password">
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter current password"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="New Password"
          rules={[
            { min: 6, message: 'Password must be at least 6 characters' },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter new password"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={['newPassword']}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm new password"
            size="large"
          />
        </Form.Item>

        {/* Action Buttons */}
        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" htmlType="submit">
              Save Changes
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};
